import { useState } from "react";
import { fmt } from "./Utils/Utilities";

// ─── Step config ────────────────────────────────────────────────────────────
const STEPS = [
  { id: 0, label: "Product & Quantity", icon: "", desc: "Select product, quantity & pricing" },
  { id: 1, label: "Confirm",   icon: "", desc: "Review & submit" },
];

const PRODUCTS = ["LPG Bulk", "LPG Cylinder", "Natural Gas", "CNG", "Propane"];
const UNITS    = ["Cylinders", "kg"];
const DRIVERS  = ["Bongani M.", "Thandi K.", "Sipho D.", "Lerato N.", "Unassigned"];

// ─── Step Indicator ──────────────────────────────────────────────────────────
function StepRail({ current }) {
  return (
    <div style={{ display: "flex", alignItems: "center", marginBottom: 28, position: "relative" }}>
      {STEPS.map((s, i) => {
        const done    = current > i;
        const active  = current === i;
        const last    = i === STEPS.length - 1;

        return (
          <div key={s.id} style={{ display: "flex", alignItems: "center", flex: last ? 0 : 1 }}>
            {/* Node */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, position: "relative", zIndex: 1 }}>
              <div style={{
                width: 38, height: 38, borderRadius: "50%",
                border: `2px solid ${done ? "var(--success)" : active ? "var(--flame)" : "var(--bright)"}`,
                background: done ? "var(--success)" : active ? "rgba(255,79,23,.12)" : "var(--deep)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 14, transition: "all .2s",
                boxShadow: active ? "0 0 0 4px rgba(255,79,23,.12)" : "none",
              }}>
                {done
                  ? <span style={{ fontSize: 14 }}>✓</span>
                  : <span style={{ fontSize: 13, opacity: active ? 1 : 0.45 }}>{s.icon}</span>
                }
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{
                  fontSize: 9, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase",
                  color: done ? "var(--success)" : active ? "var(--flame)" : "var(--text3)",
                  transition: "color .2s",
                }}>{s.label}</div>
              </div>
            </div>

            {/* Connector line */}
            {!last && (
              <div style={{
                flex: 1, height: 2, margin: "0 8px", marginBottom: 24,
                background: done
                  ? "linear-gradient(90deg, var(--success), var(--teal))"
                  : "var(--bright)",
                transition: "background .3s",
                borderRadius: 2,
              }}/>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Field wrapper ────────────────────────────────────────────────────────────
function Field({ label, children, hint }) {
  return (
    <div className="fg">
      <label className="fl">{label}</label>
      {children}
      {hint && <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function StepProductQuantity({ form, setForm }) {
  // helper to update a specific line
  const updateLine = (idx, patch) => setForm(f => ({ ...f, lines: f.lines.map((l, i) => i === idx ? { ...l, ...patch } : l) }));
  const addLine = () => {
    // pick first available product that's not already used
    const used = form.lines.map(l => l.product);
    const available = PRODUCTS.find(p => !used.includes(p)) || PRODUCTS[0];
    setForm(f => ({ ...f, lines: [...f.lines, { product: available, qty: "", unit: UNITS[0], unitPrice: "", deliveryDate: "" }] }));
  };
  const removeLine = (idx) => {
    setForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));
  };

  const total = form.lines.reduce((s, l) => s + (Number(l.qty || 0) * Number(l.unitPrice || 0)), 0);

  return (
    <div style={{ animation: "slup .18s ease" }}>
      <StepHeading step={0} />

      {form.lines.map((line, idx) => {
        const usedElsewhere = form.lines.map(l => l.product).filter((p, i) => i !== idx);
        return (
          <div key={idx} style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
              <Field label={`Product ${idx + 1}`}>
                <select className="fsel" value={line.product} onChange={e => updateLine(idx, { product: e.target.value })}>
                  {PRODUCTS.map(p => (
                    <option key={p} value={p} disabled={usedElsewhere.includes(p) && p !== line.product}>{p}</option>
                  ))}
                </select>
              </Field>

              <Field label="Quantity">
                <input className="fi" type="number" min={0} placeholder="0"
                  value={line.qty}
                  onChange={e => updateLine(idx, { qty: e.target.value })} />
              </Field>

              <Field label="Unit">
                <select className="fsel" value={line.unit} onChange={e => updateLine(idx, { unit: e.target.value })}>
                  {UNITS.map(u => <option key={u}>{u}</option>)}
                </select>
              </Field>

              <Field label="Latest Delivery">
                <input
                  className="fi"
                  type="date"
                  value={line.deliveryDate}
                  onChange={e => updateLine(idx, { deliveryDate: e.target.value })}
                />
              </Field>

              <div style={{ display: "flex", alignItems: "flex-end", gap: 8, marginBottom: 17 }}>
                <button className="btn btn-s" onClick={addLine} disabled={form.lines.length >= PRODUCTS.length} title="Add another product line">+ Add</button>
                {form.lines.length > 1 && (
                  <button className="btn btn-s" onClick={() => removeLine(idx)}>Remove</button>
                )}
              </div>
            </div>
          </div>
        );
      })}

      
    </div>
  );
}

function StepConfirm({ form }) {
  const total = form.lines.reduce((s, l) => s + (Number(l.qty || 0) * Number(l.unitPrice || 0)), 0);

  const latestDelivery = (() => {
    const dates = form.lines.map((l) => l.deliveryDate).filter(Boolean);
    if (dates.length === 0) return null;
    return dates.reduce((a, b) => (new Date(a) > new Date(b) ? a : b));
  })();

  const rows = [
    { label: "Latest Delivery", value: latestDelivery ? fmt.date(latestDelivery) : "—" },
    ...form.lines.map((l, i) => ({ label: `Product ${i + 1}`, value: `${l.product} · ${l.qty || 0} ${l.unit}` })),
  ];

  return (
    <div style={{ animation: "slup .18s ease" }}>
      <StepHeading step={1} />
      <div style={{ borderRadius: "var(--r8)", overflow: "hidden",
        border: "1px solid var(--bright)", marginBottom: 14 }}>
        {rows.map((r, i) => (
          <div key={r.label} className="fb" style={{
            padding: "10px 16px",
            borderBottom: i < rows.length - 1 ? "1px solid var(--border)" : "none",
            background: i % 2 === 0 ? "var(--deep)" : "var(--surface)",
          }}>
            <span style={{ fontSize: 11, color: "var(--text3)", fontWeight: 600,
              textTransform: "uppercase", letterSpacing: ".08em" }}>{r.label}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text1)",
              fontFamily: "inherit" }}>{r.value}</span>
          </div>
        ))}
         </div>

      {form.notes && (
        <div style={{ padding: "10px 14px", borderRadius: "var(--r8)",
          background: "var(--deep)", border: "1px solid var(--bright)" }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: ".1em",
            textTransform: "uppercase", color: "var(--text3)", marginBottom: 5 }}>Notes</div>
          <div style={{ fontSize: 12, color: "var(--text2)", lineHeight: 1.5 }}>{form.notes}</div>
        </div>
      )}
    </div>
  );
}

// ─── Step heading ─────────────────────────────────────────────────────────────
function StepHeading({ step }) {
  const s = STEPS[step];
  return (
    <div style={{ marginBottom: 20, paddingBottom: 14, borderBottom: "1px solid var(--border)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
        <span style={{ fontSize: 16 }}>{s.icon}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text1)" }}>
          Step {step + 1} of {STEPS.length} — {s.label}
        </span>
      </div>
      <div style={{ fontSize: 11, color: "var(--text3)" }}>{s.desc}</div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const EMPTY_FORM = {
  deliveryDate: "", notes: "",
  // support multiple order lines
  lines: [
    { product: "LPG Cylinder", qty: "", unit: UNITS[0], unitPrice: "", deliveryDate: "" }
  ],
};

function CreateOrderPage({ showToast, setPage }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);

  const isLast  = step === STEPS.length - 1;
  const isFirst = step === 0;

  function handleNext() {
    if (!isLast) setStep(s => s + 1);
  }
  function handleBack() {
    if (!isFirst) setStep(s => s - 1);
    else setPage("orders");
  }
  function handleConfirm() {
    const summary = form.lines.map(l => `${l.product} ${l.qty || 0} ${l.unit}`).join("; ");
    showToast?.(`Order placed: ${summary}`, "s");
    setPage("orders");
  }

  return (
    <div className="page">
      {/* Page header */}
      <div className="fb mb16">
        <div>
          <div className="ph-title">Create New Order</div>
          <div className="hdr-crumb">
            Orders / New · Step {step + 1} of {STEPS.length}
          </div>
        </div>
        <button className="btn btn-g" onClick={() => setPage("orders")}>
          ✕ Cancel
        </button>
      </div>

      <div className="panel" style={{ padding: "22px 24px" }}>
        {/* Step rail */}
        <StepRail current={step} />

        {/* Step content */}
        <div style={{ minHeight: 260 }}>
          {step === 0 && <StepProductQuantity form={form} setForm={setForm} />}
          {step === 1 && <StepConfirm  form={form} />}
        </div>

        {/* Footer nav */}
        <div className="fc" style={{
          justifyContent: "space-between", marginTop: 24,
          paddingTop: 18, borderTop: "1px solid var(--border)",
        }}>
          <button className="btn btn-g" onClick={handleBack}>
            ← {isFirst ? "Back to Orders" : "Back"}
          </button>

          {/* Step pills */}
          <div style={{ display: "flex", gap: 5 }}>
            {STEPS.map((_, i) => (
              <div key={i} style={{
                width: i === step ? 20 : 6, height: 6, borderRadius: 3,
                background: i < step ? "var(--success)"
                  : i === step ? "var(--flame)" : "var(--bright)",
                transition: "all .2s",
              }} />
            ))}
          </div>

          {isLast
            ? <button className="btn btn-p" onClick={handleConfirm}>
                ✓ Confirm Order
              </button>
            : <button className="btn btn-p" onClick={handleNext}>
                Next: {STEPS[step + 1].label} →
              </button>
          }
        </div>
      </div>
    </div>
  );
}

export default CreateOrderPage;